// src/modules/app-views/crm/pages/Boards/tabs/VoipTab/CallHistory.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useCallback, useState } from 'react';
import { Button, Empty, Spin, Tag } from 'antd';
import {
  FileTextOutlined,
  LeftOutlined,
  LoadingOutlined,
  PhoneOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { VscCallOutgoing, VscCallIncoming } from 'react-icons/vsc';
import translateX from 'utils/translateX';
import dayjs from 'dayjs';
import AudioPlayer from './AudioPlayer';
import TranscriptionModal from './TranscriptionModal';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const CallHistory = props => {
  // ---------------------------------------------------------------------------
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ---------------------------------------------------------------------------
  const { calls, loading, page, totalPages, onPageChange, onRedial, calling } = props;

  // ---------------------------------------------------------------------------
  // 4. LOCAL STATE
  // ---------------------------------------------------------------------------
  const [transcriptionModal, setTranscriptionModal] = useState({
    open: false,
    transcription: null,
    callInfo: null,
  });

  // ---------------------------------------------------------------------------
  // 6. HANDLERS
  // ---------------------------------------------------------------------------
  const handleOpenTranscription = useCallback(call => {
    setTranscriptionModal({
      open: true,
      transcription: call.transcription || null,
      callInfo: call,
    });
  }, []);

  const handleCloseTranscription = useCallback(() => {
    setTranscriptionModal({ open: false, transcription: null, callInfo: null });
  }, []);

  // ---------------------------------------------------------------------------
  // 8. UI
  // ---------------------------------------------------------------------------
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Scrollable list */}
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin>
              <div />
            </Spin>
          </div>
        ) : !calls || calls.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={translateX('goto_no_calls')}
          />
        ) : (
          calls.map(call => {
            const isInbound = call.direction === 'inbound';
            const isMissed = call.status === 'no_answer' || call.status === 'busy';
            const isFailed = call.status === 'failed' || call.status === 'cancelled';

            // Display: for outbound show destination (to), for inbound show origin (from)
            const displayNumber = isInbound ? call.from : call.to;
            const displayLabel = call.collaboratorName || displayNumber || '-';
            const redialNumber = isInbound ? call.from : call.to;

            // Status tag config
            const statusTag = getStatusTag(call.status);

            // Post-call processing: completed but audio/transcription not ready yet
            const isPostCallProcessing = call.status === 'completed' &&
              call.postCallStatus !== 'completed' &&
              call.postCallStatus !== 'failed' &&
              call.postCallStatus !== 'skipped';

            const hasAudio = !!call.attachments?.[0]?.storageUrl && !!call._id;
            const hasTranscription = !!call.transcription;

            // Direction icon
            const DirectionIcon = isInbound ? VscCallIncoming : VscCallOutgoing;
            const iconColor = isMissed || isFailed
              ? 'var(--ant-color-error)'
              : isInbound
                ? 'var(--ant-color-primary)'
                : '#52c41a';

            return (
              <div
                key={call._id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 12px',
                  borderRadius: 6,
                  marginBottom: 4,
                  background: isMissed || isFailed
                    ? 'var(--ant-color-error-bg)'
                    : 'var(--ant-color-bg-layout)',
                  border: isMissed || isFailed
                    ? '1px solid var(--ant-color-error-border)'
                    : '1px solid transparent',
                  transition: 'background 0.2s',
                }}
              >
                {/* Direction icon + info */}
                <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <DirectionIcon style={{ fontSize: 14, color: iconColor, flexShrink: 0 }} />
                    <strong
                      style={{
                        fontSize: 13,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {displayLabel}
                    </strong>
                    {statusTag && (
                      <Tag
                        color={statusTag.color}
                        variant="outlined"
                        style={{ margin: 0, fontSize: 11 }}
                      >
                        {translateX(statusTag.key)}
                      </Tag>
                    )}
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 12,
                      color: 'var(--ant-color-text-secondary)',
                      marginTop: 2,
                    }}
                  >
                    <span>
                      {call.initiatedAt
                        ? dayjs(call.initiatedAt).format('DD/MM HH:mm')
                        : '-'}
                    </span>
                    {call.durationSeconds > 0 && (
                      <span>{formatDuration(call.durationSeconds)}</span>
                    )}
                    {call.extensionNumber && (
                      <span style={{ opacity: 0.7 }}>
                        ext. {call.extensionNumber}
                      </span>
                    )}
                    {call._id && (
                      <span style={{ opacity: 0.35, fontSize: 10, marginLeft: 'auto' }}>
                        {call._id}
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions: audio + transcription (or loading) + redial */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    flexShrink: 0,
                  }}
                >
                  {isPostCallProcessing ? (
                    // Loading while audio/transcription are being processed
                    <LoadingOutlined
                      style={{ fontSize: 14, color: 'var(--ant-color-text-secondary)' }}
                    />
                  ) : (
                    <>
                      {hasAudio && <AudioPlayer callId={call._id} />}
                      {hasTranscription && (
                        <Button
                          type="text"
                          size="small"
                          icon={<FileTextOutlined />}
                          onClick={() => handleOpenTranscription(call)}
                          style={{ color: 'var(--ant-color-text-secondary)' }}
                        />
                      )}
                    </>
                  )}

                  {/* Redial button */}
                  {redialNumber && (
                    <Button
                      type="text"
                      size="small"
                      icon={<PhoneOutlined />}
                      onClick={() => onRedial?.(redialNumber)}
                      disabled={calling}
                      style={{ color: '#52c41a', flexShrink: 0 }}
                    />
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Pagination footer */}
      {totalPages > 1 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 12,
            paddingTop: 8,
            borderTop: '1px solid var(--ant-color-border)',
            marginTop: 8,
          }}
        >
          <Button
            type="text"
            size="small"
            icon={<LeftOutlined />}
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
          />
          <span style={{ fontSize: 13, color: 'var(--ant-color-text-secondary)' }}>
            {page} / {totalPages}
          </span>
          <Button
            type="text"
            size="small"
            icon={<RightOutlined />}
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
          />
        </div>
      )}

      {/* Transcription modal */}
      <TranscriptionModal
        open={transcriptionModal.open}
        onClose={handleCloseTranscription}
        transcription={transcriptionModal.transcription}
        callInfo={transcriptionModal.callInfo}
      />
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default CallHistory;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Format seconds to m:ss display string.
 */
function formatDuration(seconds) {
  if (!seconds || seconds <= 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Determine status tag for a call based on its status.
 * Returns { color, key } for Tag component (outlined variant).
 */
function getStatusTag(status) {
  switch (status) {
    case 'initiated':
    case 'ringing':
      return { color: 'processing', key: 'goto_call_calling' };
    case 'answered':
      return { color: 'success', key: 'goto_call_active' };
    case 'no_answer':
    case 'busy':
      return { color: 'error', key: 'goto_call_missed' };
    case 'failed':
    case 'cancelled':
      return { color: 'error', key: 'goto_call_failed' };
    case 'completed':
      return { color: 'green', key: 'goto_call_completed' };
    default:
      return null;
  }
}
